import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { Observable } from "rxjs";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import {
  AppSettings,
  AppSettingsService,
  blankScanReports,
  EmailReportResponse,
  MapSettings,
  mapRangesFromApi,
  mapRangesToApi,
  mapSettingsFromRanges,
  ScanReportSettings,
  scanReportsFromApi,
  MAP_STATUS_KEYS,
  MapRangeList,
  mapRangesFor,
  MapStatusKey,
  mapRangeLabel,
  mapShadeColor,
  ReportFrequency,
  ReportSendTo,
  validateMapRanges,
  validateScanReports,
} from "src/app/services/app-settings.service";
import {
  AppearanceMode,
  ThemeDef,
  ThemeService,
} from "src/app/services/theme.service";
import { DashboardService } from "src/app/services/home/dashboard.service";

type ConfigTab = "reports" | "map" | "theme";

/** A user the report can be sent to (from GetUserDropdownForConfig). */
interface ReportUser {
  usrId: number;
  name: string;
  email: string;
  initials: string;
  /** Avatar colour pair, picked from the name so it stays stable. */
  tone: { bg: string; fg: string };
}

interface FrequencyOption {
  key: ReportFrequency;
  label: string;
  description: string;
}

interface SendToOption {
  key: ReportSendTo;
  label: string;
  description: string;
}

interface MapStatusOption {
  key: MapStatusKey;
  label: string;
}

@Component({
  selector: "app-configuration",
  templateUrl: "./configuration.component.html",
  styleUrls: ["./configuration.component.css"],
})
export class ConfigurationComponent implements OnInit, OnDestroy {
  activeTab: ConfigTab = "reports";

  readonly frequencies: FrequencyOption[] = [
    { key: "daily", label: "Daily", description: "Every day" },
    { key: "weekly", label: "Weekly", description: "Once a week" },
    { key: "monthly", label: "Monthly", description: "Once a month" },
    { key: "quarterly", label: "Quarterly", description: "Every 3 months" },
    { key: "halfYearly", label: "Semi-Annual", description: "Every 6 months" },
    { key: "yearly", label: "Yearly", description: "Once a year" },
  ];

  readonly sendToOptions: SendToOption[] = [
    {
      key: "self",
      label: "My Email Only",
      description: "Send the report to your registered email address.",
    },
    {
      key: "users",
      label: "Users Under Me",
      description: "Send the report to selected users under your account.",
    },
  ];

  // ── report recipients: the company's other active users ──────────────────
  /** Full recipient list (search ""), loaded on page init (admins only). */
  users: ReportUser[] = [];
  usersLoaded = false;
  usersLoading = false;
  usersLoadError = false;
  /** Server-side search results while the picker's search box has text. */
  private searchResults: ReportUser[] = [];
  searchLoading = false;
  searchError = false;
  private searchDebounce: any = null;
  /** Every user seen in any response, by id - chips for picked users must
   *  survive the picker list being narrowed by a search. */
  private knownUsers: { [usrId: number]: ReportUser } = {};
  /** "Add Users" popover. */
  pickerOpen = false;
  pickerSearch = "";
  private static readonly AVATAR_TONES = [
    { bg: "#dbeafe", fg: "#1d4ed8" },
    { bg: "#fee2e2", fg: "#b91c1c" },
    { bg: "#dcfce7", fg: "#15803d" },
    { bg: "#ede9fe", fg: "#6d28d9" },
    { bg: "#ffedd5", fg: "#c2410c" },
    { bg: "#cffafe", fg: "#0e7490" },
    { bg: "#fce7f3", fg: "#be185d" },
  ];

  /** Same set and order as the dashboard map's own status pills - here they
   *  only switch which colour the range preview is drawn in. */
  readonly mapStatuses: MapStatusOption[] = [
    { key: "all", label: "Total Scans" },
    { key: "authentic", label: "Authentic" },
    { key: "suspected", label: "Suspected" },
    { key: "duplicate", label: "Duplicate" },
    { key: "fake", label: "Fake" },
    { key: "unverified", label: "Unverified" },
  ];

  /** Status whose colour the range editor and legend preview use. */
  previewStatus: MapStatusKey = "all";

  readonly appearanceOptions: {
    key: AppearanceMode;
    label: string;
    description: string;
  }[] = [
    { key: "light", label: "Light", description: "Clean and bright" },
    { key: "dark", label: "Dark", description: "Easy on the eyes" },
  ];

  /** Short accent description per colour theme (swatch caption). */
  private static readonly THEME_CAPTIONS: { [name: string]: string } = {
    classic: "Blue accent",
    brown: "Brown & tan",
    emerald: "Green & teal",
    violet: "Violet & pink",
    amber: "Amber & rose",
    slate: "Slate & sky",
  };

  /** Edits in progress - only written back on Save Changes. */
  draft: AppSettings;
  draftTheme: string;
  draftMode: AppearanceMode;

  isLightMode = document.body.classList.contains("light-mode");
  savedNotice = false;
  private savedNoticeTimer: any;

  private readonly onThemeChange = () => {
    this.isLightMode = document.body.classList.contains("light-mode");
  };

  constructor(
    private settingsService: AppSettingsService,
    private themeService: ThemeService,
    private location: Location,
    private router: Router,
    private dashboardService: DashboardService,
  ) {}

  ngOnInit(): void {
    this.resetDraft();
    window.addEventListener(ThemeService.THEME_EVENT, this.onThemeChange);
    if (this.isAdmin) {
      this.loadReportSettings();
      // Recipient list up front, so saved picks show as chips straight away.
      this.loadUsers("");
    } else {
      this.activeTab = "theme";
    }
  }

  // ── Automated Scan Reports: saved settings from GetConfigurationSettings ──
  /** The last settings the API returned (or that Save applied) - what
   *  Cancel goes back to. null until the first successful load. */
  private savedReports: ScanReportSettings | null = null;
  reportsLoading = false;
  reportsLoadError = false;

  get reportsReady(): boolean {
    return !!this.savedReports;
  }

  /** India Map Settings as the API last returned / accepted them. */
  private savedMap: MapSettings | null = null;
  /** GetConfigurationSettings' details.defaultRanges - what "Reset to
   *  default" restores. null if the API sent none (Reset is disabled). */
  private defaultRanges: MapRangeList | null = null;

  get canResetMap(): boolean {
    return !!this.defaultRanges;
  }
  mapLoadError = false;

  get mapReady(): boolean {
    return !!this.savedMap;
  }

  loadReportSettings(only?: "reports" | "map"): void {
    const refresh = !!only;
    const doMap = !only || only === "map";
    const doReports = !only || only === "reports";
    const compId = ConfigurationComponent.readCompId();
    const userId = ConfigurationComponent.readUsrId();
    if (!compId || !userId) {
      if (!refresh) {
        this.reportsLoadError = true;
        this.mapLoadError = true;
      }
      return;
    }
    if (!refresh) {
      this.reportsLoading = true;
      this.reportsLoadError = false;
      this.mapLoadError = false;
    }
    this.dashboardService
      .getConfigurationSettings({ compId, userId })
      .subscribe(
        (res: any) => {
          if (!refresh) this.reportsLoading = false;
          const ok =
            res && (res.responseCode === "200" || res.responseCode === 200);
          if (doMap) {
            const applied =
              ok && res.details && this.applyServerMap(res.details);
            if (!applied && !refresh) this.mapLoadError = true;
          }
          if (!doReports) return;
          const er: EmailReportResponse | null =
            ok && res.details && res.details.emailReport
              ? res.details.emailReport
              : null;
          if (!er) {
            if (!refresh) this.reportsLoadError = true;
            return;
          }
          // emailReport.enabledByCompany is deliberately ignored - only
          // isEnabled drives the switch.
          const reports = scanReportsFromApi(er);
          // Saved recipients come with name/email - chips can show them
          // before (or without) the Add Users list.
          (er.selectedUsers || []).forEach((u) => {
            const ru = this.toReportUser(u);
            if (!isNaN(ru.usrId)) this.knownUsers[ru.usrId] = ru;
          });
          this.savedReports = reports;
          this.draft.scanReports = this.cloneReports(reports);
          this.draft.scanReports.content.aiAlerts = false;
        },
        () => {
          if (refresh) return;
          this.reportsLoading = false;
          this.reportsLoadError = true;
          this.mapLoadError = true;
        },
      );
  }

  /** details -> the map tab. The company's own scanRanges if it has a usable
   *  list, else details.defaultRanges (isDefaultRanges). false if neither is
   *  usable - there is no built-in fallback. */
  private applyServerMap(details: any): boolean {
    const defaults = mapRangesFromApi(details.defaultRanges);
    if (defaults) this.defaultRanges = defaults;
    const own = details.isDefaultRanges
      ? null
      : mapRangesFromApi(details.scanRanges);
    if (
      !own &&
      Array.isArray(details.scanRanges) &&
      details.scanRanges.length &&
      !details.isDefaultRanges
    ) {
      console.warn(
        "GetConfigurationSettings: unusable scanRanges",
        details.scanRanges,
      );
    }
    const ranges = own || defaults;
    if (!ranges) return false;
    this.savedMap = mapSettingsFromRanges(ranges);
    this.draft.map = this.cloneMap(this.savedMap);
    return true;
  }

  private cloneMap(m: MapSettings): MapSettings {
    return mapSettingsFromRanges(m.ranges);
  }

  private cloneReports(r: ScanReportSettings): ScanReportSettings {
    return {
      ...r,
      content: { ...r.content },
      userIds: r.userIds.slice(),
    };
  }

  private static readCompId(): number | null {
    try {
      const raw = localStorage.getItem("user");
      const id = raw ? Number(JSON.parse(raw).compId) : NaN;
      return isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }

  /** Automated Scan Reports and India Map Settings are admin-only: the
   *  signed-in user's roleNm must be "admin", in any casing. */
  readonly isAdmin: boolean = ConfigurationComponent.readIsAdmin();

  private static readIsAdmin(): boolean {
    try {
      const raw = localStorage.getItem("user");
      const user = raw ? JSON.parse(raw) : null;
      if (!user) return false;
      const companies: any[] = Array.isArray(user.companyDetails)
        ? user.companyDetails
        : [];
      // Only the current company's role counts - no "first company"
      // fallback, which could hand admin from another company.
      const current = companies.find((c) => c && c.compId == user.compId);
      const roleNm = (current && current.roleNm) || user.roleNm;
      return (
        String(roleNm || "")
          .trim()
          .toLowerCase() === "admin"
      );
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener(ThemeService.THEME_EVENT, this.onThemeChange);
    clearTimeout(this.savedNoticeTimer);
    clearTimeout(this.searchDebounce);
  }

  selectTab(tab: ConfigTab): void {
    if ((tab === "reports" || tab === "map") && !this.isAdmin) return;
    this.activeTab = tab;
    // Save is per tab - an error from another tab's save doesn't apply here.
    this.saveError = "";
  }

  goBack(): void {
    // Opened directly (bookmark / new tab) there's nothing to go back to.
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(["/dashboard1"]);
    }
  }

  // ── Automated Scan Reports ───────────────────────────────────────────────
  get reportError(): string | null {
    return validateScanReports(this.draft.scanReports);
  }

  selectFrequency(key: ReportFrequency): void {
    if (!this.draft.scanReports.enabled) return;
    this.draft.scanReports.frequency = key;
  }

  toggleContent(key: "scanInsights" | "aiAlerts"): void {
    if (!this.draft.scanReports.enabled) return;
    const c = this.draft.scanReports.content;
    c[key] = !c[key];
  }

  selectSendTo(key: ReportSendTo): void {
    if (!this.draft.scanReports.enabled) return;
    this.draft.scanReports.sendTo = key;
    if (key !== "users") this.pickerOpen = false;
  }

  /** Users the report currently goes to (sendTo "users"). */
  get selectedUsers(): ReportUser[] {
    const r = this.draft.scanReports;
    if (r.allUsers) return this.users;
    return r.userIds.map((id) => this.knownUsers[id]).filter((u) => !!u);
  }

  /** Saved picks with no matching user in the loaded list - still loading,
   *  the load failed, or the user is no longer offered by the API. */
  get unloadedSelectedCount(): number {
    const r = this.draft.scanReports;
    if (r.allUsers) return 0;
    return r.userIds.filter((id) => !this.knownUsers[id]).length;
  }

  /** "N users selected" - from the saved ids, so it's right before the
   *  list has loaded too. */
  get selectedCountLabel(): string {
    const r = this.draft.scanReports;
    if (r.allUsers) {
      if (!this.usersLoaded) return "All users selected";
      const n = this.users.length;
      return n + (n === 1 ? " user" : " users") + " selected";
    }
    const n = r.userIds.length;
    return n + (n === 1 ? " user" : " users") + " selected";
  }

  isUserSelected(u: ReportUser): boolean {
    const r = this.draft.scanReports;
    return r.allUsers || r.userIds.indexOf(u.usrId) !== -1;
  }

  toggleSelectAll(): void {
    const r = this.draft.scanReports;
    r.allUsers = !r.allUsers;
    if (!r.allUsers) r.userIds = [];
  }

  /** Picking or un-picking one user ends "all users" - the rest stay
   *  selected as an explicit list. */
  toggleUser(u: ReportUser): void {
    const r = this.draft.scanReports;
    const ids = r.allUsers ? this.users.map((x) => x.usrId) : r.userIds.slice();
    const at = ids.indexOf(u.usrId);
    if (at === -1) ids.push(u.usrId);
    else ids.splice(at, 1);
    r.allUsers = false;
    r.userIds = ids;
    // everyone picked one by one reads as "Select All" again
    if (this.users.length && ids.length === this.users.length) {
      r.allUsers = true;
    }
  }

  removeUser(u: ReportUser): void {
    if (this.isUserSelected(u)) this.toggleUser(u);
  }

  togglePicker(ev: Event): void {
    ev.stopPropagation();
    this.pickerOpen = !this.pickerOpen;
    if (!this.pickerOpen) return;
    this.pickerSearch = "";
    this.resetSearch();
    // Loaded on page init; only re-fetch here if that load failed.
    if (!this.usersLoaded && !this.usersLoading) this.loadUsers("");
  }

  /** Search is server-side (the API's `search`), debounced. Its loading /
   *  error state is the picker's own (searchLoading/searchError) - never
   *  the page-level usersLoading. */
  onPickerSearch(term: string): void {
    this.pickerSearch = term;
    clearTimeout(this.searchDebounce);
    const q = term.trim();
    if (!q) {
      // Back to the full list already in memory - no request needed.
      this.resetSearch();
      return;
    }
    this.searchLoading = true;
    this.searchError = false;
    this.searchDebounce = setTimeout(() => this.loadUsers(q), 350);
  }

  private resetSearch(): void {
    clearTimeout(this.searchDebounce);
    this.searchLoading = false;
    this.searchError = false;
    this.searchResults = [];
  }

  retryUsers(): void {
    this.loadUsers(this.pickerSearch.trim());
  }

  get isSearching(): boolean {
    return !!this.pickerSearch.trim();
  }

  /** What the picker's list shows as loading / failed - search state while
   *  there's a query, else the initial full-list load. */
  get pickerLoading(): boolean {
    return this.isSearching ? this.searchLoading : this.usersLoading;
  }

  get pickerError(): boolean {
    return this.isSearching ? this.searchError : this.usersLoadError;
  }

  get pickerUsers(): ReportUser[] {
    return this.isSearching ? this.searchResults : this.users;
  }

  @HostListener("document:click")
  closePicker(): void {
    this.pickerOpen = false;
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.pickerOpen = false;
  }

  /** Recipients for the signed-in user from Home/GetUserDropdownForConfig.
   *  search "" is the full list (kept in `users`, which Select All uses);
   *  anything else only feeds the picker's results. */
  loadUsers(search: string): void {
    const userId = ConfigurationComponent.readUsrId();
    // Full list (search "") drives the page-level state; a search only the
    // picker's own.
    const setLoading = (v: boolean) =>
      search ? (this.searchLoading = v) : (this.usersLoading = v);
    const setError = (v: boolean) =>
      search ? (this.searchError = v) : (this.usersLoadError = v);
    if (!userId) {
      setLoading(false);
      setError(true);
      return;
    }
    setLoading(true);
    setError(false);
    this.dashboardService
      .getUserDropdownForConfig({ userId, search })
      .subscribe(
        (res: any) => {
          // A newer search may have been typed since: a stale search
          // response is ignored; the full list is always kept.
          const isLatest = !search || search === this.pickerSearch.trim();
          if (isLatest) setLoading(false);
          const ok =
            res && (res.responseCode === "200" || res.responseCode === 200);
          const list = ok ? this.extractUserList(res.details) : null;
          if (!list) {
            if (isLatest) setError(true);
            return;
          }
          const mapped = list
            .filter((u: any) => this.userIdOf(u) !== userId)
            .map((u: any) => this.toReportUser(u))
            .filter((u: ReportUser) => !isNaN(u.usrId))
            .sort((x: ReportUser, y: ReportUser) =>
              x.name.localeCompare(y.name),
            );
          mapped.forEach((u: ReportUser) => (this.knownUsers[u.usrId] = u));
          if (search) {
            if (isLatest) this.searchResults = mapped;
          } else {
            this.users = mapped;
            this.usersLoaded = true;
          }
        },
        () => {
          if (search && search !== this.pickerSearch.trim()) return;
          setLoading(false);
          setError(true);
        },
      );
  }

  /** Signed-in user's usrId from localStorage "user" - null if missing. */
  private static readUsrId(): number | null {
    try {
      const raw = localStorage.getItem("user");
      const id = raw ? Number(JSON.parse(raw).usrId) : NaN;
      return isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }

  /** The list may come back as details itself or wrapped in details.data. */
  private extractUserList(details: any): any[] | null {
    if (Array.isArray(details)) return details;
    if (details && Array.isArray(details.data)) return details.data;
    return null;
  }

  private userIdOf(u: any): number {
    return Number(u.usrId != null ? u.usrId : u.userId);
  }

  private toReportUser(u: any): ReportUser {
    const email = u.usrEmail || u.email || u.emailId || "";
    const name =
      [u.fstNm, u.lstNm].filter((x: any) => x && String(x).trim()).join(" ") ||
      u.userName ||
      u.name ||
      u.usrNm ||
      email ||
      String(this.userIdOf(u));
    const parts = String(name).trim().split(/\s+/);
    const initials = (
      (parts[0] || "").charAt(0) +
      (parts.length > 1 ? parts[parts.length - 1].charAt(0) : "")
    ).toUpperCase();
    let hash = 0;
    for (let i = 0; i < name.length; i++)
      hash = (hash * 31 + name.charCodeAt(i)) | 0;
    const tones = ConfigurationComponent.AVATAR_TONES;
    return {
      usrId: this.userIdOf(u),
      name,
      email,
      initials: initials || "?",
      tone: tones[Math.abs(hash) % tones.length],
    };
  }

  trackByUser(_: number, u: ReportUser): number {
    return u.usrId;
  }

  // ── India map scan-count ranges ──────────────────────────────────────────
  /** The list being edited: the shared one, or the selected status's own. */
  get editRanges(): MapRangeList {
    return mapRangesFor(this.draft.map, this.previewStatus);
  }

  /** Problem with the list on screen (drives the legend preview). */
  get editRangesError(): string | null {
    return validateMapRanges(this.editRanges);
  }

  /** First problem in anything that would be saved; per-status messages
   *  name the status so it's findable from another pill. */
  get mapRangeError(): string | null {
    const map = this.draft.map;
    if (map.sameForAll) return validateMapRanges(map.ranges);
    const current = validateMapRanges(this.editRanges);
    if (current) return current;
    for (const k of MAP_STATUS_KEYS) {
      const err = validateMapRanges(map.statusRanges[k]);
      if (err) return `${this.statusLabel(k)}: ${err}`;
    }
    return null;
  }

  statusLabel(key: MapStatusKey): string {
    const s = this.mapStatuses.find((x) => x.key === key);
    return s ? s.label : key;
  }

  selectPreviewStatus(key: MapStatusKey): void {
    this.previewStatus = key;
  }

  previewLabel(): string {
    return this.statusLabel(this.previewStatus);
  }

  rangeColor(shade: number): string {
    return mapShadeColor(this.previewStatus, shade);
  }

  rangeLabel(i: number): string {
    return mapRangeLabel(this.editRanges, i);
  }

  /** A band's upper bound: the next band's start - 1 (null for the top
   *  band, which has no upper limit). */
  rangeToValue(i: number): number | null {
    const next = this.editRanges[i + 1];
    if (!next) return null;
    const n = Number(next.min) - 1;
    return Number.isFinite(n) ? n : null;
  }

  /** Band indexes highest first - the order the map's legend lists them. */
  get legendOrder(): number[] {
    return this.editRanges.map((_, i) => i).reverse();
  }

  setRangeMin(i: number, value: any): void {
    if (i === 0) return; // the first band's start is fixed (from the API)
    const n = value === "" || value === null ? NaN : Math.floor(Number(value));
    this.editRanges[i].min = n;
  }

  /** Editing a band's "To" moves the next band's start to To + 1, keeping
   *  the ranges continuous. */
  setRangeTo(i: number, value: any): void {
    const next = this.editRanges[i + 1];
    if (!next) return; // the top band is open-ended
    const n = value === "" || value === null ? NaN : Math.floor(Number(value));
    next.min = n + 1;
  }

 
  blockNonInteger(event: KeyboardEvent): void {
    if ([".", ",", "e", "E", "+", "-"].includes(event.key)) {
      event.preventDefault();
    }
  }

 
  blockNonIntegerPaste(event: ClipboardEvent): void {
    const text = event.clipboardData ? event.clipboardData.getData("text") : "";
    if (!/^\d+$/.test(text.trim())) event.preventDefault();
  }

  /** Resets only the list on screen (shared, or the selected status's). */
  /** "Reset to default": the API's details.defaultRanges (not saved until
   *  Save Changes). */
  resetMapRanges(): void {
    if (!this.defaultRanges) return;
    this.draft.map = mapSettingsFromRanges(this.defaultRanges);
  }

  trackByIndex(i: number): number {
    return i;
  }

  get themes(): ThemeDef[] {
    return this.themeService.themes;
  }

  selectTheme(name: string): void {
    this.draftTheme = name;
  }

  themeCaption(t: ThemeDef): string {
    return ConfigurationComponent.THEME_CAPTIONS[t.name] || "Accent colour";
  }

  selectMode(mode: AppearanceMode): void {
    this.draftMode = mode;
  }

  cancel(): void {
    this.resetDraft();
    this.pickerOpen = false;
    this.saveError = "";
  }

  /** Saves only the tab on screen: Map -> SaveScanRangeConfig, Automated
   *  Scan Reports -> SaveEmailReportConfig, Theme -> applied locally. Edits
   *  still pending on the other tabs are left as they are. */
  save(): void {
    if (this.saving) return;
    this.saveError = "";

    if (this.activeTab === "theme") {
      if (this.draftTheme !== this.themeService.activeTheme) {
        this.themeService.applyTheme(this.draftTheme);
      }
      if (this.draftMode !== this.themeService.mode) {
        this.themeService.setMode(this.draftMode);
      }
      this.showSavedNotice();
      return;
    }

    // Reports / map tabs are admin-only and need their loaded settings.
    const tab = this.activeTab;
    if (!this.isAdmin) return;
    if (tab === "reports" && !this.reportsReady) return;
    if (tab === "map" && !this.mapReady) return;
    if (tab === "reports" && this.reportError) return; // shown inline
    if (tab === "map" && this.mapRangeError) return; // shown inline

    const compId = ConfigurationComponent.readCompId();
    const userId = ConfigurationComponent.readUsrId();
    if (!compId || !userId) {
      this.saveError = "Couldn't save - please sign in again.";
      return;
    }

    const r = this.draft.scanReports;
    if (
      tab === "reports" &&
      r.enabled &&
      r.sendTo === "users" &&
      r.allUsers &&
      !this.usersLoaded
    ) {
      // Select All is sent as the full id list - which we don't have yet.
      this.saveError = this.usersLoadError
        ? "Couldn't load the user list for Select All Users - retry from Add Users."
        : "Still loading users for Select All Users - try again in a moment.";
      return;
    }

    const reports = this.cloneReports(r);
    const mapSnapshot = this.cloneMap(this.draft.map);
    const request: Observable<any> =
      tab === "map"
        ? this.dashboardService.saveScanRangeConfig({
            compId,
            userId,
            ranges: mapRangesToApi(mapSnapshot.ranges),
          })
        : this.dashboardService.saveEmailReportConfig(
            this.buildEmailReportPayload(r, compId, userId),
          );
    const what = tab === "map" ? "map settings" : "report settings";

    this.saving = true;
    request.subscribe(
      (res: any) => {
        this.saving = false;
        const ok =
          res && (res.responseCode === "200" || res.responseCode === 200);
        if (!ok) {
          // Keep the unsaved edits on screen so they can retry.
          this.saveError =
            (res && res.responseMessage) ||
            `Couldn't save ${what}. Please try again.`;
          return;
        }
        // The draft already matches what was saved - it just becomes the
        // new baseline for Cancel.
        if (tab === "map") {
          this.savedMap = mapSnapshot;
        } else {
          this.savedReports = reports;
        }
        this.showSavedNotice();
        // Re-read what the server actually stored for this tab.
        this.loadReportSettings(tab);
      },
      () => {
        this.saving = false;
        this.saveError = `Couldn't save ${what}. Please try again.`;
      },
    );
  }

  saving = false;
  saveError = "";

  private showSavedNotice(): void {
    this.savedNotice = true;
    clearTimeout(this.savedNoticeTimer);
    this.savedNoticeTimer = setTimeout(() => (this.savedNotice = false), 2600);
  }

  /** ReportFrequency -> the API's frequency text. */
  private static readonly FREQUENCY_API: { [k: string]: string } = {
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly",
    quarterly: "Quarterly",
    halfYearly: "SemiAnnual",
    yearly: "Yearly",
  };

  /** SaveEmailReportConfig body. "My Email Only" -> MyEmail with no ids;
   *  "Users Under Me" -> SelectedUsers with the picked ids - or, with
   *  Select All on, every user in the loaded list (the API has no "all
   *  users" type). */
  private buildEmailReportPayload(
    r: ScanReportSettings,
    compId: number,
    userId: number,
  ) {
    const toUsers = r.sendTo === "users";
    const ids = !toUsers
      ? []
      : r.allUsers
        ? this.users.map((u) => u.usrId)
        : r.userIds.slice();
    return {
      compId,
      userId,
      isEnabled: r.enabled,
      frequency: ConfigurationComponent.FREQUENCY_API[r.frequency] || "Daily",
      includeBrandInsights: r.content.scanInsights,
      // "Coming soon" - never sent as on.
      includeAIAlerts: false,
      recipientType: (toUsers ? "SelectedUsers" : "MyEmail") as
        | "MyEmail"
        | "SelectedUsers",
      recipientUserIds: ids,
    };
  }

  private resetDraft(): void {
    this.draft = this.settingsService.get();
    // Map ranges come from the API (savedMap); the placeholder only stands in
    // until it has answered - and the map tab isn't shown until then.
    if (this.savedMap) this.draft.map = this.cloneMap(this.savedMap);
    // Report settings only ever come from the API (savedReports) - blank
    // until it has answered.
    this.draft.scanReports = this.savedReports
      ? this.cloneReports(this.savedReports)
      : blankScanReports();
    // AI-Powered Alerts is "coming soon" - never send it as selected.
    this.draft.scanReports.content.aiAlerts = false;
    this.draftTheme = this.themeService.activeTheme;
    this.draftMode = this.themeService.mode;
  }
}
